import React, { useEffect, useState } from "react"
import { useParams, Link } from "react-router-dom"
import { approvePayout, rejectPayout, getAdminPayoutById } from "../../services/wallet"
import {
  ArrowLeftIcon,
  CheckCircleIcon,
  XCircleIcon,
  BanknotesIcon,
  BuildingOfficeIcon,
  CreditCardIcon,
  CalendarIcon,
  UserIcon,
  DocumentTextIcon,
  XMarkIcon
} from "@heroicons/react/24/outline"

const AdminPayoutDetails = () => {
  const { payoutId } = useParams()
  const [payout, setPayout] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState("")
  const [success, setSuccess] = useState("")
  const [processing, setProcessing] = useState(false)
  const [showRejectModal, setShowRejectModal] = useState(false)
  const [rejectionReason, setRejectionReason] = useState("")

  const fetchPayout = async () => {
    setLoading(true)
    setError("")
    setSuccess("")
    try {
      const res = await getAdminPayoutById(payoutId)
      if (res.status === 200) {
        setPayout(res.data)
      } else {
        setError(res.error || "Payout not found")
      }
    } catch (err) {
      console.error('Error fetching payout:', err)
      setError("Failed to fetch payout details. Please try again.")
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => { fetchPayout() }, [payoutId])

  const handleApprove = async () => {
    if (!window.confirm("Are you sure you want to approve and pay this payout?")) return

    setProcessing(true)
    setError("")
    try {
      const res = await approvePayout(payoutId)
      if (res.status === 200) {
        setSuccess("Payout approved and paid successfully")
        fetchPayout()
      } else {
        setError(res.error || "Action failed")
      }
    } catch (err) {
      setError("Action failed")
    }
    setProcessing(false)
  }

  const handleReject = async () => {
    if (!rejectionReason.trim()) {
      setError("Please provide a reason for rejection")
      return
    }

    setProcessing(true)
    setError("")
    setSuccess("")
    try {
      const res = await rejectPayout(payoutId, rejectionReason.trim())
      if (res.status === 200) {
        setSuccess("Payout rejected successfully")
        setShowRejectModal(false)
        setRejectionReason("")
        fetchPayout()
      } else {
        setError(res.error || "Action failed")
      }
    } catch (err) {
      setError("Action failed")
    }
    setProcessing(false)
  }

  const openRejectModal = () => {
    setShowRejectModal(true)
    setRejectionReason("")
    setError("")
  }

  const closeRejectModal = () => {
    setShowRejectModal(false)
    setRejectionReason("")
    setError("")
  }

  if (loading) {
    return (
      <div className="text-center py-20">
        <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-cyan-600"></div>
        <p className="mt-2 text-slate-500">Loading payout details...</p>
      </div>
    )
  }

  if (!payout) {
    return (
      <div className="max-w-4xl mx-auto">
        <Link to="/admin/payouts" className="inline-flex items-center gap-2 text-slate-500 hover:text-slate-900 mb-6">
          <ArrowLeftIcon className="w-4 h-4" /> Back to Payouts
        </Link>
        <div className="text-center py-20">
          <p className="text-slate-500">Payout not found</p>
        </div>
      </div>
    )
  }

  return (
    <div className="max-w-4xl mx-auto">
      <Link to="/admin/payouts" className="inline-flex items-center gap-2 text-slate-500 hover:text-slate-900 mb-6">
        <ArrowLeftIcon className="w-4 h-4" /> Back to Payouts
      </Link>

      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
        {/* Header Section */}
        <div className="p-8 border-b border-slate-100 bg-gradient-to-r from-cyan-50 to-blue-50">
          <div className="flex items-start justify-between mb-6">
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 rounded-xl bg-cyan-100 flex items-center justify-center">
                <BanknotesIcon className="w-8 h-8 text-cyan-600" />
              </div>
              <div>
                <h1 className="text-3xl font-bold text-slate-900">Payout Review</h1>
                <p className="text-slate-500 mt-1">Payout ID: {payout._id}</p>
              </div>
            </div>
            <span className={`inline-flex items-center px-4 py-2 rounded-lg text-sm font-bold uppercase ${
              payout.status === 'paid' || payout.status === 'approved' ? 'bg-green-100 text-green-700' :
              payout.status === 'pending' ? 'bg-amber-100 text-amber-700' :
              'bg-red-100 text-red-700'
            }`}>
              {payout.status?.toUpperCase() || 'UNKNOWN'}
            </span>
          </div>

          <div className="flex flex-col items-center bg-white rounded-xl p-6 border border-slate-200">
            <p className="text-slate-500 font-medium text-sm mb-2">Withdrawal Amount</p>
            <h2 className="text-5xl font-bold text-slate-900">₦{payout.amount?.toLocaleString()}</h2>
            <p className="text-slate-500 text-sm mt-2">{payout.currency || 'NGN'}</p>
          </div>
        </div>

        {/* Messages */}
        {error && (
          <div className="mx-8 mt-6 p-4 bg-red-50 text-red-600 rounded-xl border border-red-100 flex items-center gap-3">
            <XCircleIcon className="w-5 h-5 shrink-0" />
            <span>{error}</span>
            <button onClick={() => setError("")} className="ml-auto">
              <XMarkIcon className="w-5 h-5" />
            </button>
          </div>
        )}
        {success && (
          <div className="mx-8 mt-6 p-4 bg-green-50 text-green-600 rounded-xl border border-green-100 flex items-center gap-3">
            <CheckCircleIcon className="w-5 h-5 shrink-0" />
            <span>{success}</span>
            <button onClick={() => setSuccess("")} className="ml-auto">
              <XMarkIcon className="w-5 h-5" />
            </button>
          </div>
        )}

        <div className="p-8">
          {/* Bank Account Information */}
          <div className="mb-8">
            <h3 className="text-sm font-bold text-slate-900 uppercase tracking-wider mb-4">Bank Account Details</h3>
            <div className="bg-slate-50 rounded-xl p-6 border border-slate-200">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <p className="text-xs font-bold text-slate-500 uppercase mb-1">Account Name</p>
                  <p className="text-slate-900 font-semibold text-lg">
                    {payout.centerId?.accountName || payout.accountName || 'Not provided'}
                  </p>
                </div>
                <div>
                  <p className="text-xs font-bold text-slate-500 uppercase mb-1">Bank Name</p>
                  <p className="text-slate-900 font-medium">
                    {payout.centerId?.bank || payout.bankName || 'Not provided'}
                  </p>
                </div>
                <div className="md:col-span-2">
                  <p className="text-xs font-bold text-slate-500 uppercase mb-1">Account Number</p>
                  <p className="font-mono text-slate-900 font-semibold text-xl">
                    {payout.centerId?.accountNumber || payout.accountNumber || 'Not provided'}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Center Information */}
          <div className="mb-8">
            <h3 className="text-sm font-bold text-slate-900 uppercase tracking-wider mb-4">Center Information</h3>
            <div className="bg-slate-50 rounded-xl p-6 border border-slate-200">
              <div className="flex items-start gap-3">
                <BuildingOfficeIcon className="w-5 h-5 text-slate-400 mt-0.5" />
                <div className="flex-1">
                  <p className="text-slate-900 font-semibold text-lg">
                    {payout.centerId?.organization || payout.centerName || 'Unknown Center'}
                  </p>
                  {payout.centerId?.email && (
                    <p className="text-slate-600 text-sm mt-1">{payout.centerId.email}</p>
                  )}
                  {payout.centerId?.phoneNumber && (
                    <p className="text-slate-600 text-sm">{payout.centerId.phoneNumber}</p>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Payment & Order Information */}
          <div className="mb-8 grid grid-cols-1 md:grid-cols-2 gap-6">
            {payout.paymentId && (
              <div>
                <h3 className="text-sm font-bold text-slate-900 uppercase tracking-wider mb-4">Payment Information</h3>
                <div className="bg-slate-50 rounded-xl p-6 border border-slate-200">
                  <div className="flex items-start gap-3 mb-4">
                    <CreditCardIcon className="w-5 h-5 text-slate-400 mt-0.5" />
                    <div className="flex-1">
                      <p className="text-xs font-bold text-slate-500 uppercase mb-1">Transaction Reference</p>
                      <p className="font-mono text-slate-900 font-medium text-sm">
                        {payout.paymentId?.txRef || payout.paymentId?.transactionId || 'N/A'}
                      </p>
                    </div>
                  </div>
                  {payout.paymentId?.amount && (
                    <div className="mt-4">
                      <p className="text-xs font-bold text-slate-500 uppercase mb-1">Payment Amount</p>
                      <p className="text-slate-900 font-semibold">₦{payout.paymentId.amount.toLocaleString()}</p>
                    </div>
                  )}
                  {payout.paymentId?.status && (
                    <div className="mt-4">
                      <p className="text-xs font-bold text-slate-500 uppercase mb-1">Payment Status</p>
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        payout.paymentId.status === 'PAID' || payout.paymentId.status === 'SUCCESS' ? 'bg-green-100 text-green-700' :
                        'bg-slate-100 text-slate-600'
                      }`}>
                        {payout.paymentId.status}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            )}

            <div>
              <h3 className="text-sm font-bold text-slate-900 uppercase tracking-wider mb-4">Request Details</h3>
              <div className="bg-slate-50 rounded-xl p-6 border border-slate-200 space-y-4">
                <div className="flex items-start gap-3">
                  <CalendarIcon className="w-5 h-5 text-slate-400 mt-0.5" />
                  <div>
                    <p className="text-xs font-bold text-slate-500 uppercase mb-1">Requested At</p>
                    <p className="text-slate-900 font-medium">
                      {payout.createdAt ? new Date(payout.createdAt).toLocaleString('en-US', {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit'
                      }) : 'N/A'}
                    </p>
                  </div>
                </div>
                {payout.approvedAt && (
                  <div className="flex items-start gap-3">
                    <CheckCircleIcon className="w-5 h-5 text-green-600 mt-0.5" />
                    <div>
                      <p className="text-xs font-bold text-slate-500 uppercase mb-1">Approved At</p>
                      <p className="text-slate-900 font-medium">
                        {new Date(payout.approvedAt).toLocaleString('en-US', {
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </p>
                      {payout.approvedBy && (
                        <p className="text-slate-600 text-xs mt-1">
                          By: {payout.approvedBy?.name || payout.approvedBy?.email || 'Admin'}
                        </p>
                      )}
                    </div>
                  </div>
                )}
                {payout.rejectedAt && (
                  <div className="flex items-start gap-3">
                    <XCircleIcon className="w-5 h-5 text-red-600 mt-0.5" />
                    <div>
                      <p className="text-xs font-bold text-slate-500 uppercase mb-1">Rejected At</p>
                      <p className="text-slate-900 font-medium">
                        {new Date(payout.rejectedAt).toLocaleString('en-US', {
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </p>
                      {payout.rejectedBy && (
                        <p className="text-slate-600 text-xs mt-1">
                          By: {payout.rejectedBy?.name || payout.rejectedBy?.email || 'Admin'}
                        </p>
                      )}
                      {payout.rejectionReason && (
                        <div className="mt-2 p-3 bg-red-50 rounded-lg border border-red-100">
                          <p className="text-xs font-bold text-red-700 uppercase mb-1">Rejection Reason</p>
                          <p className="text-red-600 text-sm">{payout.rejectionReason}</p>
                        </div>
                      )}
                    </div>
                  </div>
                )}
                {payout.paidAt && (
                  <div className="flex items-start gap-3">
                    <BanknotesIcon className="w-5 h-5 text-green-600 mt-0.5" />
                    <div>
                      <p className="text-xs font-bold text-slate-500 uppercase mb-1">Paid At</p>
                      <p className="text-slate-900 font-medium">
                        {new Date(payout.paidAt).toLocaleString('en-US', {
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          {payout.status === 'pending' && (
            <div className="pt-8 border-t border-slate-100">
              <div className="flex flex-col sm:flex-row gap-4">
                <button
                  onClick={openRejectModal}
                  disabled={processing}
                  className="flex-1 flex items-center justify-center gap-2 py-3 border-2 border-red-200 text-red-600 font-bold rounded-xl hover:bg-red-50 transition disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <XCircleIcon className="w-5 h-5" />
                  Reject Payout
                </button>
                <button
                  onClick={handleApprove}
                  disabled={processing}
                  className="flex-1 flex items-center justify-center gap-2 py-3 bg-green-600 text-white font-bold rounded-xl hover:bg-green-700 transition shadow-lg shadow-green-200 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <CheckCircleIcon className="w-5 h-5" />
                  {processing ? 'Processing...' : 'Approve & Pay'}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Rejection Modal */}
      {showRejectModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-xl max-w-md w-full p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-bold text-slate-900">Reject Payout</h3>
              <button
                onClick={closeRejectModal}
                className="text-slate-400 hover:text-slate-600"
              >
                <XMarkIcon className="w-6 h-6" />
              </button>
            </div>
            <p className="text-slate-600 mb-4">
              Please provide a reason for rejecting this payout. This will be visible to the center.
            </p>
            <div className="mb-4">
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Rejection Reason <span className="text-red-500">*</span>
              </label>
              <textarea
                value={rejectionReason}
                onChange={(e) => setRejectionReason(e.target.value)}
                placeholder="Enter reason for rejection..."
                rows={4}
                className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent"
                required
              />
            </div>
            <div className="flex gap-3">
              <button
                onClick={closeRejectModal}
                className="flex-1 px-4 py-2 border border-slate-300 text-slate-700 font-medium rounded-lg hover:bg-slate-50 transition"
              >
                Cancel
              </button>
              <button
                onClick={handleReject}
                disabled={processing || !rejectionReason.trim()}
                className="flex-1 px-4 py-2 bg-red-600 text-white font-medium rounded-lg hover:bg-red-700 transition disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {processing ? 'Rejecting...' : 'Reject Payout'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default AdminPayoutDetails
